import React, { useState } from 'react';
import { X, Calendar, Users, AlertCircle, Save } from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import Button from '../ui/Button';
import { format } from 'date-fns';

interface Profile {
  user_id: string;
  sales_name: string | null;
  squad_id: string | null;
}

interface BulkVacationModalProps {
  sdrs: Profile[];
  onClose: () => void;
  onSave: () => void;
}

const BulkVacationModal: React.FC<BulkVacationModalProps> = ({ sdrs, onClose, onSave }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedSdrIds, setSelectedSdrIds] = useState<string[]>([]);

  const generateDateRange = (start: string, end: string): string[] => {
    const dates: string[] = [];
    const startDateObj = new Date(start);
    const endDateObj = new Date(end);

    const currentDate = new Date(startDateObj);
    while (currentDate <= endDateObj) {
      dates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  };

  const toggleSdrSelection = (sdrId: string) => {
    setSelectedSdrIds(prev =>
      prev.includes(sdrId)
        ? prev.filter(id => id !== sdrId)
        : [...prev, sdrId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedSdrIds.length === sdrs.length) {
      setSelectedSdrIds([]);
    } else {
      setSelectedSdrIds(sdrs.map(s => s.user_id));
    }
  };

  const selectedSdrs = sdrs.filter(sdr => selectedSdrIds.includes(sdr.user_id));

  const handlePreview = () => {
    if (!startDate || !endDate) {
      setError('Veuillez sélectionner une date de début et une date de fin');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError('La date de début doit être antérieure à la date de fin');
      return;
    }

    if (selectedSdrIds.length === 0) {
      setError('Veuillez sélectionner au moins un SDR');
      return;
    }

    setError(null);
    setShowConfirmation(true);
  };

  const handleSave = async () => {
    if (!startDate || !endDate) return;

    try {
      setIsSaving(true);
      setError(null);

      const dates = generateDateRange(startDate, endDate);

      const schedulesToInsert = [];
      for (const sdr of selectedSdrs) {
        for (const date of dates) {
          schedulesToInsert.push({
            sdr_id: sdr.user_id,
            squad_id: sdr.squad_id,
            client_id: null,
            date: date,
            day_type: 'congé',
            status: 'planned'
          });
        }
      }

      const { error: deleteError } = await supabase
        .from('prospecting_schedule')
        .delete()
        .in('sdr_id', selectedSdrIds)
        .gte('date', startDate)
        .lte('date', endDate);

      if (deleteError) throw deleteError;

      const { error: insertError } = await supabase
        .from('prospecting_schedule')
        .insert(schedulesToInsert);

      if (insertError) throw insertError;

      await onSave();
      onClose();
    } catch (err: any) {
      console.error('Error saving vacation days:', err);
      setError(err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const dateCount = startDate && endDate ? generateDateRange(startDate, endDate).length : 0;
  const totalEntries = dateCount * selectedSdrIds.length;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Calendar className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Vacances Globales</h2>
              <p className="text-sm text-gray-600">Attribuer des vacances à tous les SDRs</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {!showConfirmation ? (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Cette action marquera tous les SDRs comme non disponibles</p>
                  <p>Les plannings existants sur ces dates seront remplacés par des jours de vacances.</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de début
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de fin
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Sélectionnez les SDRs
                    </label>
                    <button
                      type="button"
                      onClick={toggleSelectAll}
                      className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                    >
                      {selectedSdrIds.length === sdrs.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                    </button>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                    {sdrs.map((sdr) => (
                      <label
                        key={sdr.user_id}
                        className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedSdrIds.includes(sdr.user_id)}
                          onChange={() => toggleSdrSelection(sdr.user_id)}
                          className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-700 flex-1">
                          {sdr.sales_name || 'Sans nom'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">SDRs sélectionnés</span>
                    <span className="font-semibold text-gray-900">{selectedSdrIds.length} / {sdrs.length}</span>
                  </div>
                  {dateCount > 0 && (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Nombre de jours</span>
                        <span className="font-semibold text-gray-900">{dateCount}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-200">
                        <span className="text-gray-600">Total d'entrées à créer</span>
                        <span className="font-bold text-orange-600">{totalEntries}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={handlePreview}
                  variant="primary"
                  disabled={!startDate || !endDate || selectedSdrIds.length === 0}
                  className="flex-1"
                >
                  Aperçu et confirmation
                </Button>
                <Button onClick={onClose} variant="secondary">
                  Annuler
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-4">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h3 className="font-semibold text-orange-900 mb-3 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    Confirmation requise
                  </h3>
                  <div className="space-y-2 text-sm text-orange-800">
                    <p>Vous êtes sur le point de :</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Marquer <strong>{selectedSdrIds.length} SDR{selectedSdrIds.length > 1 ? 's' : ''}</strong> comme non disponibles</li>
                      <li>
                        Du <strong>{format(new Date(startDate), 'dd/MM/yyyy')}</strong> au{' '}
                        <strong>{format(new Date(endDate), 'dd/MM/yyyy')}</strong>
                      </li>
                      <li>Créer <strong>{totalEntries} entrée{totalEntries > 1 ? 's' : ''}</strong> de vacances</li>
                      <li>Supprimer les plannings existants sur ces dates pour les SDRs sélectionnés</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    SDRs sélectionnés ({selectedSdrIds.length})
                  </h4>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {selectedSdrs.map((sdr) => (
                      <div
                        key={sdr.user_id}
                        className="text-sm text-gray-700 flex items-center gap-2 py-1"
                      >
                        <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                        {sdr.sales_name || 'Sans nom'}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={handleSave}
                  variant="primary"
                  disabled={isSaving}
                  icon={<Save className="h-4 w-4" />}
                  className="flex-1"
                >
                  {isSaving ? 'Enregistrement...' : 'Confirmer et enregistrer'}
                </Button>
                <Button
                  onClick={() => setShowConfirmation(false)}
                  variant="secondary"
                  disabled={isSaving}
                >
                  Retour
                </Button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default BulkVacationModal;
